import { Fragment, createContext, useContext, useEffect } from 'react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import { createEmotionTheme } from './emotionTheme';
import { theme, tokens } from './theme';

// Font imports
import GothamMedium from 'assets/fonts/gotham-medium.woff2';
import GothamBook from 'assets/fonts/gotham-book.woff2';

const ThemeContext = createContext({});

// Font styles
const fontStyles = `
  @font-face {
    font-family: "Gotham";
    font-weight: 400;
    src: url(${GothamBook}) format("woff2");
    font-display: swap;
  }

  @font-face {
    font-family: "Gotham";
    font-weight: 500;
    src: url(${GothamMedium}) format("woff2");
    font-display: swap;
  }
`;

// Create CSS custom properties from theme tokens
const createThemeStyleObject = themeTokens => {
  const style = {};

  Object.keys(themeTokens).forEach(key => {
    if (key !== 'themeId' && typeof themeTokens[key] === 'string') {
      style[`--${key}`] = themeTokens[key];
    }
  });

  return style;
};

// Generate CSS custom properties for all responsive tokens
const createTokenStyles = () => {
  const tokenStyles = Object.keys(tokens.base)
    .map(key => {
      if (typeof tokens.base[key] === 'string') {
        return `--${key}: ${tokens.base[key]};`;
      }
      return '';
    })
    .join('\n');

  const mediaStyles = Object.keys(tokens)
    .filter(key => key !== 'base')
    .map(breakpoint => {
      const breakpointTokens = tokens[breakpoint];
      const styles = Object.keys(breakpointTokens)
        .map(key => {
          if (typeof breakpointTokens[key] === 'string') {
            return `--${key}: ${breakpointTokens[key]};`;
          }
          return '';
        })
        .join('\n');

      const mediaQuery = (() => {
        switch (breakpoint) {
          case 'desktop':
            return '(min-width: 1440px)';
          case 'laptop':
            return '(max-width: 1200px)';
          case 'tablet':
            return '(max-width: 1024px)';
          case 'mobile':
            return '(max-width: 768px)';
          case 'mobileS':
            return '(max-width: 400px)';
          default:
            return null;
        }
      })();

      return mediaQuery ? `@media ${mediaQuery} { :root { ${styles} } }` : '';
    })
    .join('\n');

  return `:root { ${tokenStyles} } ${mediaStyles}`;
};

// Create theme-specific styles
const createThemeStyles = themeId => {
  const themeTokens = theme[themeId];
  const themeStyles = Object.keys(themeTokens)
    .map(key => {
      if (key !== 'themeId' && typeof themeTokens[key] === 'string') {
        return `--${key}: ${themeTokens[key]};`;
      }
      return '';
    })
    .join('\n');

  return `:root { ${themeStyles} }`;
};

const tokenStyles = createTokenStyles();

const ThemeProvider = ({
  themeId = 'dark',
  theme: themeOverrides,
  children,
  className,
  as: Component = 'div',
}) => {
  const currentTheme = { ...theme[themeId], ...themeOverrides };
  const parentTheme = useTheme();
  const isRootProvider = !parentTheme.themeId;

  // Create Emotion theme
  const emotionTheme = createEmotionTheme(themeId);

  // Create theme-specific styles
  const themeStyles = createThemeStyles(themeId);

  // Save root theme id to localstorage and apply class to body
  useEffect(() => {
    if (isRootProvider) {
      window.localStorage.setItem('theme', JSON.stringify(themeId));
      document.body.classList.remove('light', 'dark');
      document.body.classList.add(themeId);
    }
  }, [themeId, isRootProvider]);

  return (
    <ThemeContext.Provider value={currentTheme}>
      <EmotionThemeProvider theme={emotionTheme}>
        {/* Add fonts and base tokens for the root provider */}
        {isRootProvider && (
          <Fragment>
            <Helmet>
              <link rel="preload" href={GothamMedium} as="font" crossorigin="" />
              <link rel="preload" href={GothamBook} as="font" crossorigin="" />
              <style>{fontStyles}</style>
              <style>{tokenStyles}</style>
              <style>{themeStyles}</style>
            </Helmet>
            {children}
          </Fragment>
        )}
        {/* Nested providers need a div to override theme tokens */}
        {!isRootProvider && (
          <Component
            className={classNames('theme-provider', className)}
            style={createThemeStyleObject(currentTheme)}
          >
            {children}
          </Component>
        )}
      </EmotionThemeProvider>
    </ThemeContext.Provider>
  );
};

// Hook to access the theme
export const useTheme = () => useContext(ThemeContext);

// Hook to access the Emotion theme
export const useEmotionTheme = () => {
  const theme = useContext(ThemeContext);
  return createEmotionTheme(theme.themeId || 'dark');
};

export { tokens } from './theme';
export { createEmotionTheme, mixins } from './emotionTheme';
export default ThemeProvider;
